import type {
  SpecificationGroup,
  SpecificationInlineLink,
  SpecTableValueWithLink
} from '~/utils/shop/specTableValueWithLink';
import type { SpecType } from '@hypercodestudio/basler-components';

import type {
  AttributeSetFragment,
  AttributeSetMetadataFragment,
  AttributeSetMetadataUiOptionsFragment,
  CustomAttributeV2Fragment,
  GroupInterface,
  Maybe
} from '~~/lib/Shop/generated/schema';
import {
  isDateString,
  isDateTimeString,
  mapToFormattedDate
} from '~/utils/mapper/mapToFormattedDate';
import type { LOCALE_CODE } from '~~/lib/ContentfulService';
import { isDefined } from '~/utils/guards/isDefined';
import { isAttributeSelectedOptions } from '~/utils/shop/guards/isAttributeSelectedOptions';
import { isAttributeValue } from '~/utils/shop/guards/isAttributeValue';
import { isBooleanValue } from '~/utils/shop/guards/isBooleanValue';
import { getBooleanAttributeOptionLabel } from '~/utils/shop/booleanAttributeMetadata';

export type {
  SpecificationGroup,
  SpecificationInlineLink,
  SpecTableValueWithLink
} from '~/utils/shop/specTableValueWithLink';

export function flipAttributeSetMapping(
  cfg: Record<string, number>
): Record<number, string> {
  const ret: Record<number, string> = {};
  for (const key in cfg) {
    ret[cfg[key]] = key;
  }
  return ret;
}

function createGroup(
  group: Maybe<GroupInterface> | undefined,
  attributeMetadata: AttributeSetMetadataFragment[],
  customAttributesMap: Record<string, CustomAttributeV2Fragment>,
  type: SpecType,
  locale: LOCALE_CODE,
  link?: SpecificationInlineLink,
  excludedAttributeCodes?: ReadonlyArray<string>
): SpecificationGroup {
  const filteredMetadata = excludedAttributeCodes?.length
    ? attributeMetadata.filter(
        (attr) => !excludedAttributeCodes.includes(attr.code ?? '')
      )
    : attributeMetadata;

  return {
    title: group?.attribute_group_name ?? '',
    type,
    specs: filteredMetadata
      ?.map((attribute) =>
        createEntry(attribute, customAttributesMap, locale, link)
      )
      .filter(isDefined)
      .filter((x) => x.value !== '')
  };
}

/**
 * Extracts the correct label for a boolean value from attribute_set metadata
 * @param attributeCode - The attribute code to look for
 * @param attributeSet - The attribute set containing metadata
 * @param currentValue - The current boolean value ("0" or "1")
 * @returns The corresponding label or the original value if no match found
 */
function extractBooleanLabelFromAttributeSet(
  attributeCode: string,
  attributeSet: Array<{
    attribute_metadata?: Array<{
      code?: string | null;
      ui_input?: any;
    } | null> | null;
  } | null>,
  currentValue: string,
  locale: LOCALE_CODE
): string {
  // Find the attribute metadata in the attribute set
  for (const attrGroup of attributeSet) {
    if (!attrGroup?.attribute_metadata) {
      continue;
    }

    const attributeMetadata = attrGroup.attribute_metadata.find(
      (metadata) => metadata?.code === attributeCode
    );

    if (
      attributeMetadata?.ui_input &&
      hasAttributeOptions(attributeMetadata.ui_input)
    ) {
      const matchingOption = attributeMetadata.ui_input.attribute_options?.find(
        (option: any) => option?.value === currentValue
      );

      if (matchingOption?.label) {
        return matchingOption.label;
      }
    }
  }

  const cachedLabel = getBooleanAttributeOptionLabel(
    locale,
    attributeCode,
    currentValue
  );
  if (cachedLabel) {
    return cachedLabel;
  }

  // Return original value if no matching label was found.
  return currentValue;
}

export function extractLabelValueFromCustomAttribute(
  customAttribute: Readonly<CustomAttributeV2Fragment>,
  locale: LOCALE_CODE,
  attributeSet?: Array<{
    attribute_metadata?: Array<{
      code?: string | null;
      ui_input?: any;
    } | null> | null;
  } | null>
): {
  value: string | undefined | null;
  label: string;
  key: string | undefined | null;
} {
  const label = customAttribute?.label ?? '';
  let value: string | undefined | null = undefined;
  if (isAttributeSelectedOptions(customAttribute)) {
    value = customAttribute?.selected_options
      ?.map((option: { label?: string | null } | null) => {
        if (option === null) {
          return undefined;
        }
        return typeof option?.label === 'string' ? option?.label : undefined;
      })
      .filter(isDefined)
      ?.join(' ');
  }
  if (isAttributeValue(customAttribute)) {
    value = customAttribute.value;
  }

  if (isBooleanValue(customAttribute)) {
    const normalizedValue = customAttribute.value ?? '';
    if (!normalizedValue) {
      value = '';
    } else {
      value = normalizedValue;
    }
    // Use the new function to get the correct label from attribute_set
    if (normalizedValue && attributeSet && customAttribute.code) {
      value = extractBooleanLabelFromAttributeSet(
        customAttribute.code,
        attributeSet,
        normalizedValue,
        locale
      );
    } else if (normalizedValue && customAttribute.code) {
      // Fallback for list APIs that no longer include attribute_set
      value =
        getBooleanAttributeOptionLabel(
          locale,
          customAttribute.code,
          normalizedValue
        ) ?? (normalizedValue === '1' ? '1' : '0');
    }
  }

  if (isDefined(value) && (isDateTimeString(value) || isDateString(value))) {
    value = mapToFormattedDate(value, locale);
  }
  return { label, value, key: customAttribute?.code };
}

function hasAttributeOptions(
  input?: unknown | undefined | null
): input is AttributeSetMetadataUiOptionsFragment {
  return (
    typeof input === 'object' && input != null && 'attribute_options' in input
  );
}

function createEntry(
  attribute: AttributeSetMetadataFragment,
  customAttributesMap: Record<string, CustomAttributeV2Fragment>,
  locale: LOCALE_CODE,
  link?: SpecificationInlineLink
): SpecTableValueWithLink {
  let options =
    attribute.selected_attribute_options?.attribute_option
      ?.map((entry) => entry?.label)
      .filter(isDefined) ?? [];

  let currentValue = attribute.entered_attribute_value?.value;

  // Fallback to custom_attributesV2 when attribute_set values were stripped
  // by server-side deduplication
  if (!currentValue && options.length === 0 && attribute.code) {
    const customAttr = customAttributesMap[attribute.code];
    if (customAttr) {
      if (isAttributeSelectedOptions(customAttr)) {
        options =
          customAttr.selected_options
            ?.map((opt: { label?: string | null } | null) => opt?.label)
            .filter(isDefined) ?? [];
      }
      if (isAttributeValue(customAttr)) {
        currentValue = customAttr.value;
      }
    }
  }

  // The current value might be a technical value, e.g. "0".
  // We resolve it via ui_input from the attribute_set (boolean labels etc.).
  // @see https://gcp.baslerweb.com/jira/browse/WEB2-2461
  let valueLabel = currentValue;
  if (currentValue && hasAttributeOptions(attribute.ui_input)) {
    valueLabel =
      attribute.ui_input?.attribute_options?.find(
        (option) => option?.value === currentValue
      )?.label || currentValue;
  }
  if (
    attribute.code &&
    currentValue &&
    (!valueLabel || valueLabel === currentValue)
  ) {
    valueLabel =
      getBooleanAttributeOptionLabel(locale, attribute.code, currentValue) ??
      valueLabel;
  }

  if (
    isDefined(valueLabel) &&
    (isDateTimeString(valueLabel) || isDateString(valueLabel))
  ) {
    valueLabel = mapToFormattedDate(valueLabel, locale);
  }

  if (options?.length < 2) {
    const value: string = options[0] ?? valueLabel ?? '';
    if (
      (attribute.code === 'sensor_framerate' ||
        attribute.code === 'cameradata_linerate') &&
      link
    ) {
      return {
        value,
        label: attribute.label ?? '',
        inlineLink: link
      };
    }

    return {
      value,
      label: attribute.label ?? ''
    };
  }

  const value = options?.join('</li><li>') ?? valueLabel ?? '';

  return {
    value: `<ul><li>${value}</li></ul>`,
    label: attribute.label ?? ''
  };
}

export function createSpecifications(
  attributeSet: AttributeSetFragment[],
  customAttributes: ReadonlyArray<CustomAttributeV2Fragment>,
  type: SpecType,
  locale: LOCALE_CODE,
  link?: SpecificationInlineLink,
  excludedAttributeCodes?: ReadonlyArray<string>
): SpecificationGroup[] {
  const customAttributesMap: Record<string, CustomAttributeV2Fragment> = {};
  for (const attr of customAttributes) {
    if (attr.code) {
      customAttributesMap[attr.code] = attr;
    }
  }

  return attributeSet
    .map((attr) =>
      createGroup(
        attr.group,
        attr.attribute_metadata?.filter(isDefined) ?? [],
        customAttributesMap,
        type,
        locale,
        link,
        excludedAttributeCodes
      )
    )
    .flat()
    .filter(
      (group) =>
        group.specs?.length !== 0 &&
        group.specs?.some((spec) => spec.value !== '')
    );
}
